import type { PaigeExportTiming } from './export-types';

/**
 * What the Export timing card says, in CapturePoint's words ("When documents leave": As soon as they're ready, Once
 * a day, Only when I say; "Nothing leaves until you click Export now") where Paige means the same, and Paige's where
 * it differs: the daily time is the company's, not this computer's, and Pause exports holds everything.
 */

/** "21:00" as people read it here: "9:00 PM". */
export function timeLabel(value: string): string {
  const [hour, minute] = value.split(':').map(Number);
  if (
    hour === undefined ||
    minute === undefined ||
    !Number.isFinite(hour) ||
    !Number.isFinite(minute)
  )
    return value;
  return new Date(2000, 0, 1, hour, minute).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** The company's zone as a person reads it: "America/New_York" is "New York time". */
export function zoneLabel(zone: string): string {
  const city = zone.split('/').at(-1)?.replace(/_/g, ' ');
  return city ? `${city} time` : 'your company’s time';
}

/** The one line under the choices, saying what the choice means. */
export function captionOf(timing: Pick<PaigeExportTiming, 'when' | 'at' | 'timeZone'>): string {
  if (timing.when === 'ready')
    return 'Each document leaves on its own the moment it’s ready, all day.';
  if (timing.when === 'daily')
    return `Ready documents wait in Paige and leave together at ${timeLabel(timing.at ?? '18:00')}, ${zoneLabel(timing.timeZone)}.`;
  return 'Ready documents wait in Paige until someone clicks Export now.';
}

/** "1 document" / "12 documents". */
export const documents = (count: number) =>
  `${count.toLocaleString()} ${count === 1 ? 'document' : 'documents'}`;

/** What waits, said honestly: paused, waiting for the daily time, or waiting for Export now; null when nothing waits. */
export function heldLine(
  timing: Pick<PaigeExportTiming, 'held' | 'paused' | 'nextRelease' | 'when'>,
): string | null {
  if (timing.paused)
    return timing.held > 0
      ? `Exports are paused. ${documents(timing.held)} wait here; nothing is lost.`
      : 'Exports are paused. Documents wait here until you resume; nothing is lost.';
  if (timing.held === 0) return null;
  if (timing.nextRelease) {
    const at = new Date(timing.nextRelease).toLocaleString(undefined, {
      weekday: 'short',
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${documents(timing.held)} ready · they leave ${at}`;
  }
  return `${documents(timing.held)} ready`;
}
